import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Check,
  ClipboardPaste,
  ClipboardX,
  X,
  LoaderCircle,
  ListChecks,
  GripVertical
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@univdiam/ui/components/badge'
import { Button } from '@univdiam/ui/components/button'
import { Checkbox } from '@univdiam/ui/components/checkbox'
import { Input } from '@univdiam/ui/components/input'
import { Label } from '@univdiam/ui/components/label'
import { Progress } from '@univdiam/ui/components/progress'
import { Separator } from '@univdiam/ui/components/separator'
import { Skeleton } from '@univdiam/ui/components/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@univdiam/ui/components/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@univdiam/ui/components/tooltip'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@univdiam/ui/components/empty'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@univdiam/ui/components/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@univdiam/ui/components/alert-dialog'
import { cn } from '@univdiam/ui/lib/utils'
import SectionHeader from './SectionHeader'

export type ChecklistItem = {
  id: number
  title: string
  description?: string | null
  is_checked: boolean | number
}

// Tabs carry no description — `order_checklist_tabs` has no such column and the
// create/rename/import endpoints drop it. Only items have one.
export type ChecklistTab = {
  id: number
  tab_name: string
  checklist: ChecklistItem[]
}

export type ChecklistTabPayload = {
  tab_name: string
  checklist: { title: string; description?: string | null; is_checked: boolean }[]
}

const CLIPBOARD_KEY = 'univdiam-checklist-clipboard'
// How long a removed item stays undoable before the delete actually fires.
const UNDO_WINDOW_MS = 5000

const readClipboard = (): ChecklistTabPayload[] => {
  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as ChecklistTabPayload[]) : []
  } catch {
    return []
  }
}

type ChecklistTabsProps = {
  tabs: ChecklistTab[]
  isAdmin?: boolean | null
  isLoading?: boolean
  onAddTab?: (tab_name: string) => Promise<unknown> | void
  onRenameTab?: (tab: ChecklistTab, tab_name: string) => Promise<unknown> | void
  onDeleteTab?: (tabId: number) => Promise<unknown> | void
  onAddItem?: (tabId: number, title: string) => Promise<unknown> | void
  onToggleItem?: (item: ChecklistItem, checked: boolean) => void
  onEditItem?: (item: ChecklistItem, title: string) => Promise<unknown> | void
  onReorderItems?: (tabId: number, itemIds: number[]) => Promise<unknown> | void
  onDeleteItem?: (itemId: number) => Promise<unknown> | void
  onPasteTabs?: (tabs: ChecklistTabPayload[]) => Promise<unknown> | void
}

const ChecklistTabs = ({
  tabs,
  isAdmin,
  isLoading,
  onAddTab,
  onRenameTab,
  onDeleteTab,
  onAddItem,
  onToggleItem,
  onEditItem,
  onReorderItems,
  onDeleteItem,
  onPasteTabs
}: ChecklistTabsProps) => {
  const [activeTabId, setActiveTabId] = useState<number | undefined>(tabs[0]?.id)

  // Items removed but still inside their undo window — hidden locally, not deleted yet.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([])

  const visibleItems = (tab?: ChecklistTab) =>
    (tab?.checklist ?? []).filter((item) => !pendingDeleteIds.includes(item.id))

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0]

  // Keep selection valid: jump to a freshly added/pasted tab, fall back if the
  // active one was deleted. Keyed on ids so a new-but-equal array can't loop.
  const tabIdsKey = tabs.map((tab) => tab.id).join(',')
  const prevTabIdsRef = useRef<number[]>([])
  useEffect(() => {
    const ids = tabs.map((tab) => tab.id)
    const prev = prevTabIdsRef.current
    prevTabIdsRef.current = ids

    const added = prev.length > 0 ? ids.filter((id) => !prev.includes(id)) : []
    if (added.length > 0) {
      setActiveTabId(added[0])
      return
    }

    setActiveTabId((current) => (current != null && ids.includes(current) ? current : ids[0]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIdsKey])

  const progress = useMemo(() => {
    let done = 0
    let total = 0
    for (const tab of tabs) {
      for (const item of visibleItems(tab)) {
        total += 1
        if (item.is_checked) done += 1
      }
    }
    return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, pendingDeleteIds])

  // Add/rename tab shares one dialog — same form, different submit target.
  const [tabDialogOpen, setTabDialogOpen] = useState(false)
  const [tabBeingRenamed, setTabBeingRenamed] = useState<ChecklistTab | null>(null)
  const [tabNameInput, setTabNameInput] = useState('')
  const [isSavingTab, setIsSavingTab] = useState(false)
  const savingTabRef = useRef(false)

  const openAddTabDialog = () => {
    setTabBeingRenamed(null)
    setTabNameInput('')
    setTabDialogOpen(true)
  }

  const openRenameTabDialog = (tab: ChecklistTab) => {
    setTabBeingRenamed(tab)
    setTabNameInput(tab.tab_name)
    setTabDialogOpen(true)
  }

  const submitTabDialog = async () => {
    if (savingTabRef.current) return
    const tab_name = tabNameInput.trim()
    if (!tab_name) return

    savingTabRef.current = true
    setIsSavingTab(true)
    try {
      if (tabBeingRenamed) await onRenameTab?.(tabBeingRenamed, tab_name)
      else await onAddTab?.(tab_name)
      setTabDialogOpen(false)
    } catch {
      // mutation already surfaces its own error toast; keep the dialog open to retry
    } finally {
      savingTabRef.current = false
      setIsSavingTab(false)
    }
  }

  const [tabPendingDelete, setTabPendingDelete] = useState<ChecklistTab | null>(null)
  const [isDeletingTab, setIsDeletingTab] = useState(false)

  const confirmDeleteTab = async () => {
    if (!tabPendingDelete) return
    setIsDeletingTab(true)
    try {
      await onDeleteTab?.(tabPendingDelete.id)
      setTabPendingDelete(null)
    } catch {
      // mutation already surfaces its own error toast; keep the dialog open to retry
    } finally {
      setIsDeletingTab(false)
    }
  }

  // A single checklist item is cheap to re-add, so skip the modal: hide it now,
  // fire the delete once the undo window closes.
  const deleteTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>())
  const onDeleteItemRef = useRef(onDeleteItem)
  onDeleteItemRef.current = onDeleteItem

  useEffect(
    () => () => {
      // Unmounting mid-undo commits the deletes rather than silently dropping them.
      deleteTimersRef.current.forEach((timer, id) => {
        clearTimeout(timer)
        void onDeleteItemRef.current?.(id)
      })
      deleteTimersRef.current.clear()
    },
    []
  )

  const commitDeleteItem = (itemId: number) => {
    deleteTimersRef.current.delete(itemId)
    const clear = () => setPendingDeleteIds((ids) => ids.filter((id) => id !== itemId))
    // On failure the parent toasts and the item reappears.
    Promise.resolve(onDeleteItemRef.current?.(itemId)).then(clear, clear)
  }

  const undoDeleteItem = (itemId: number) => {
    const timer = deleteTimersRef.current.get(itemId)
    if (timer) clearTimeout(timer)
    deleteTimersRef.current.delete(itemId)
    setPendingDeleteIds((ids) => ids.filter((id) => id !== itemId))
  }

  const requestDeleteItem = (item: ChecklistItem) => {
    if (deleteTimersRef.current.has(item.id)) return
    setPendingDeleteIds((ids) => [...ids, item.id])
    deleteTimersRef.current.set(
      item.id,
      setTimeout(() => commitDeleteItem(item.id), UNDO_WINDOW_MS)
    )
    toast(`Removed "${item.title}"`, {
      duration: UNDO_WINDOW_MS,
      action: { label: 'Undo', onClick: () => undoDeleteItem(item.id) }
    })
  }

  // Copy the whole checklist (all tabs + items) to a browser-local clipboard;
  // paste applies it to whatever order is open, no target picker needed. The
  // clipboard survives pasting so one checklist can seed many orders.
  const [clipboardTabCount, setClipboardTabCount] = useState(0)
  const hasClipboard = clipboardTabCount > 0
  useEffect(() => {
    const sync = () => setClipboardTabCount(readClipboard().length)
    sync()
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])

  const [isPasting, setIsPasting] = useState(false)
  const [pasteConfirmOpen, setPasteConfirmOpen] = useState(false)
  const [justCopied, setJustCopied] = useState(false)

  const copyAllTabs = () => {
    const payload: ChecklistTabPayload[] = tabs.map((tab) => ({
      tab_name: tab.tab_name,
      checklist: visibleItems(tab).map((item) => ({
        title: item.title,
        description: item.description,
        is_checked: Boolean(item.is_checked)
      }))
    }))
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload))
    setClipboardTabCount(payload.length)
    setJustCopied(true)
    setTimeout(() => setJustCopied(false), 2000)
    toast.success(
      `Copied ${payload.length} tab${payload.length === 1 ? '' : 's'} — open another order to paste`
    )
  }

  const discardClipboard = () => {
    localStorage.removeItem(CLIPBOARD_KEY)
    setClipboardTabCount(0)
    toast('Copied checklist discarded')
  }

  // Pasting appends, so confirm first when the order already has tabs.
  const requestPaste = () => {
    if (tabs.length > 0) setPasteConfirmOpen(true)
    else void pasteAllTabs()
  }

  const pasteAllTabs = async () => {
    const payload = readClipboard()
    if (payload.length === 0) return
    setIsPasting(true)
    try {
      await onPasteTabs?.(payload)
      setPasteConfirmOpen(false)
    } catch {
      // mutation already surfaces its own error toast; the clipboard is kept either way
    } finally {
      setIsPasting(false)
    }
  }

  // Inline edit of one item's title — the row's label swaps for an input.
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [editTitleInput, setEditTitleInput] = useState('')
  const [isSavingItem, setIsSavingItem] = useState(false)
  const savingItemRef = useRef(false)

  const startEditItem = (item: ChecklistItem) => {
    setEditingItemId(item.id)
    setEditTitleInput(item.title)
  }

  const submitEditItem = async (item: ChecklistItem) => {
    if (savingItemRef.current) return
    const title = editTitleInput.trim()
    if (!title || title === item.title) {
      setEditingItemId(null)
      return
    }

    savingItemRef.current = true
    setIsSavingItem(true)
    try {
      await onEditItem?.(item, title)
      setEditingItemId(null)
    } catch {
      // mutation already surfaces its own error toast; leave the input open to retry
    } finally {
      savingItemRef.current = false
      setIsSavingItem(false)
    }
  }

  // Drag-to-reorder items within a tab — same HTML5 drag handle as the
  // additional-fields list on the Add Product form.
  const dragItemIndex = useRef<number | null>(null)
  const [dragOverItemId, setDragOverItemId] = useState<number | null>(null)

  const dropItemAt = (tab: ChecklistTab, toIndex: number) => {
    const from = dragItemIndex.current
    dragItemIndex.current = null
    setDragOverItemId(null)
    if (from === null || from === toIndex) return

    const ids = visibleItems(tab).map((item) => item.id)
    const [moved] = ids.splice(from, 1)
    if (moved === undefined) return
    ids.splice(toIndex, 0, moved)
    void onReorderItems?.(tab.id, ids)
  }

  // Inline add-item input — appears in place of the "Add checklist item" button
  // and stays open after each save so a list can be typed in one pass.
  const [addingItemTabId, setAddingItemTabId] = useState<number | null>(null)
  const [itemTitleInput, setItemTitleInput] = useState('')
  const [isAddingItem, setIsAddingItem] = useState(false)
  const addingItemRef = useRef(false)
  const itemInputRef = useRef<HTMLInputElement>(null)

  const startAddItem = (tabId: number) => {
    setAddingItemTabId(tabId)
    setItemTitleInput('')
  }

  const submitAddItem = async (keepOpen: boolean) => {
    if (addingItemRef.current) return
    const title = itemTitleInput.trim()
    if (!title || addingItemTabId == null) {
      setAddingItemTabId(null)
      return
    }

    addingItemRef.current = true
    setIsAddingItem(true)
    try {
      await onAddItem?.(addingItemTabId, title)
      setItemTitleInput('')
      if (keepOpen) requestAnimationFrame(() => itemInputRef.current?.focus())
      else setAddingItemTabId(null)
    } catch {
      // mutation already surfaces its own error toast; leave the input open to retry
    } finally {
      addingItemRef.current = false
      setIsAddingItem(false)
    }
  }

  const headerActions = isAdmin ? (
    <>
      {hasClipboard && (
        <>
          <Button variant='outline' size='sm' onClick={requestPaste} disabled={isPasting}>
            {isPasting ? <LoaderCircle className='animate-spin' /> : <ClipboardPaste />}
            Paste Checklist
            <Badge variant='secondary' className='h-5 min-w-5 justify-center rounded-full border-0 px-1.5 text-caption2 font-normal tabular-nums'>
              {clipboardTabCount}
            </Badge>
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='size-8 text-muted-foreground'
                onClick={discardClipboard}
                aria-label='Discard copied checklist'
              >
                <ClipboardX />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Discard copied checklist</TooltipContent>
          </Tooltip>
        </>
      )}
      {tabs.length > 0 && (
        <Button variant='outline' size='sm' onClick={copyAllTabs}>
          {justCopied ? <Check className='text-emerald-600' /> : <Copy />}
          {justCopied ? 'Copied' : 'Copy Checklist'}
        </Button>
      )}
    </>
  ) : null

  const showSkeleton = isLoading && tabs.length === 0

  const renderTabBody = (tab: ChecklistTab) => {
    const items = visibleItems(tab)

    return (
      <>
        {items.length === 0 && (
          <Empty className='border-0 p-6'>
            <EmptyHeader>
              <EmptyTitle className='text-base'>Nothing on this tab</EmptyTitle>
              <EmptyDescription>
                {isAdmin ? 'Add the first checklist item below.' : 'No items have been added yet.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {items.map((item, index) => (
          <div
            key={item.id}
            onDragOver={(e) => {
              if (dragItemIndex.current === null) return
              e.preventDefault()
              if (dragOverItemId !== item.id) setDragOverItemId(item.id)
            }}
            onDrop={(e) => {
              e.preventDefault()
              dropItemAt(tab, index)
            }}
            className={cn(
              'group -mx-2 flex items-start gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/40',
              dragOverItemId === item.id && 'bg-muted'
            )}
          >
            {isAdmin && (
              <button
                type='button'
                draggable
                onDragStart={() => {
                  dragItemIndex.current = index
                }}
                onDragEnd={() => {
                  dragItemIndex.current = null
                  setDragOverItemId(null)
                }}
                aria-label={`Reorder ${item.title}`}
                className='mt-0.5 -ml-1 cursor-grab text-muted-foreground/60 hover:text-foreground active:cursor-grabbing transition-opacity md:opacity-0 md:group-hover:opacity-100'
              >
                <GripVertical className='size-4' />
              </button>
            )}
            <Checkbox
              id={`checklist-item-${item.id}`}
              className='mt-0.5'
              checked={Boolean(item.is_checked)}
              onCheckedChange={(checked) => onToggleItem?.(item, Boolean(checked))}
            />
            {editingItemId === item.id ? (
              <div className='flex flex-1 min-w-0 items-center gap-2'>
                <Input
                  autoFocus
                  value={editTitleInput}
                  onChange={(e) => setEditTitleInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void submitEditItem(item)
                    }
                    if (e.key === 'Escape') setEditingItemId(null)
                  }}
                  onBlur={() => void submitEditItem(item)}
                  placeholder='Checklist item title'
                  className='h-8 text-body2'
                  disabled={isSavingItem}
                />
                {isSavingItem && (
                  <LoaderCircle className='w-4 h-4 animate-spin text-muted-foreground shrink-0' />
                )}
              </div>
            ) : (
              <Label
                htmlFor={`checklist-item-${item.id}`}
                className='flex-1 min-w-0 flex-col items-start gap-0.5 font-normal cursor-pointer'
              >
                <span
                  className={cn(
                    'text-body2 leading-snug',
                    item.is_checked && 'line-through text-muted-foreground'
                  )}
                >
                  {item.title}
                </span>
                {item.description && (
                  <span className='text-caption1 text-muted-foreground leading-snug'>
                    {item.description}
                  </span>
                )}
              </Label>
            )}
            {isAdmin && editingItemId !== item.id && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label={`Edit ${item.title}`}
                      onClick={() => startEditItem(item)}
                      className='size-7 text-muted-foreground hover:text-foreground transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100'
                    >
                      <Pencil />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit item</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label={`Remove ${item.title}`}
                      onClick={() => requestDeleteItem(item)}
                      className='size-7 text-muted-foreground hover:text-destructive transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100'
                    >
                      <X />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Remove item</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        ))}

        {isAdmin &&
          (addingItemTabId === tab.id ? (
            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <Input
                  ref={itemInputRef}
                  autoFocus
                  value={itemTitleInput}
                  onChange={(e) => setItemTitleInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void submitAddItem(true)
                    }
                    if (e.key === 'Escape') {
                      setItemTitleInput('')
                      setAddingItemTabId(null)
                    }
                  }}
                  onBlur={() => void submitAddItem(false)}
                  placeholder='Checklist item title'
                  className='text-body2'
                  disabled={isAddingItem}
                />
                {isAddingItem && (
                  <LoaderCircle className='w-4 h-4 animate-spin text-muted-foreground shrink-0' />
                )}
              </div>
              <span className='text-caption2 text-muted-foreground'>
                Enter to add another · Esc to finish
              </span>
            </div>
          ) : (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => startAddItem(tab.id)}
              className='mt-1 self-start text-muted-foreground hover:text-foreground -ml-2'
            >
              <Plus /> Add checklist item
            </Button>
          ))}
      </>
    )
  }

  return (
    <div className='flex flex-col bg-white border border-[#E9EAEB] rounded-md text-caption1 overflow-hidden'>
      <SectionHeader
        title='Checklist'
        sectionIcon={<ListChecks strokeWidth={1.5} className='w-5 h-5 text-gray-600' />}
        isAdmin={isAdmin}
        headerActions={headerActions}
      />

      {progress.total > 0 && (
        <div className='px-4 py-3 flex items-center gap-3 border-b border-[#E9EAEB]'>
          <Progress
            value={progress.pct}
            aria-label='Checklist progress'
            className={cn(
              'h-1.5 flex-1',
              progress.done === progress.total && '[&>div]:bg-emerald-500'
            )}
          />
          <span className='text-caption1 tabular-nums text-muted-foreground shrink-0'>
            {progress.done}/{progress.total} done
          </span>
        </div>
      )}

      {showSkeleton && (
        <div className='p-4 flex flex-col gap-4'>
          <div className='flex gap-2'>
            <Skeleton className='h-8 w-28' />
            <Skeleton className='h-8 w-20' />
          </div>
          <div className='flex flex-col gap-3'>
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className='flex items-center gap-3'>
                <Skeleton className='size-4 rounded-[4px]' />
                <Skeleton className='h-3' style={{ width: `${70 - row * 12}%` }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!showSkeleton && tabs.length === 0 && (
        <Empty className='border-0'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ListChecks strokeWidth={1.5} />
            </EmptyMedia>
            <EmptyTitle>No checklist yet</EmptyTitle>
            <EmptyDescription>
              {isAdmin
                ? 'Add a tab to start tracking the steps for this order.'
                : 'Nothing has been added to this order yet.'}
            </EmptyDescription>
          </EmptyHeader>
          {isAdmin && (
            <EmptyContent>
              <div className='flex items-center justify-center gap-2'>
                <Button size='sm' onClick={openAddTabDialog}>
                  <Plus /> Add tab
                </Button>
                {hasClipboard && (
                  <Button size='sm' variant='outline' onClick={requestPaste} disabled={isPasting}>
                    {isPasting ? <LoaderCircle className='animate-spin' /> : <ClipboardPaste />}
                    Paste checklist
                  </Button>
                )}
              </div>
            </EmptyContent>
          )}
        </Empty>
      )}

      {!showSkeleton && tabs.length > 0 && activeTab && (
        <Tabs
          value={String(activeTab.id)}
          onValueChange={(value) => setActiveTabId(Number(value))}
          className='gap-0'
        >
          {/* Tab strip scrolls on overflow; the tab-level actions stay pinned beside it. */}
          <div className='flex items-center gap-2 px-4 py-2 border-b border-[#E9EAEB]'>
            <div className='min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
              <TabsList className='h-9'>
                {tabs.map((tab) => {
                  const items = visibleItems(tab)
                  const done = items.filter((item) => item.is_checked).length
                  const isComplete = items.length > 0 && done === items.length

                  return (
                    <TabsTrigger key={tab.id} value={String(tab.id)} className='flex-none'>
                      {tab.tab_name}
                      {items.length > 0 && (
                        <Badge
                          variant='secondary'
                          className={cn(
                            'h-5 min-w-9 justify-center rounded-full border-0 px-1.5 text-caption2 font-normal tabular-nums',
                            isComplete
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {done}/{items.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>

            {isAdmin && (
              <div className='flex items-center gap-1 shrink-0'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-8 text-muted-foreground'
                      onClick={openAddTabDialog}
                      aria-label='Add tab'
                    >
                      <Plus />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add tab</TooltipContent>
                </Tooltip>

                <Separator orientation='vertical' className='!h-5' />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-8 text-muted-foreground'
                      onClick={() => openRenameTabDialog(activeTab)}
                      aria-label={`Rename tab ${activeTab.tab_name}`}
                    >
                      <Pencil />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Rename tab</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-8 text-muted-foreground hover:text-destructive'
                      onClick={() => setTabPendingDelete(activeTab)}
                      aria-label={`Delete tab ${activeTab.tab_name}`}
                    >
                      <Trash2 />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete tab</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={String(tab.id)} className='p-4 flex flex-col gap-1'>
              {renderTabBody(tab)}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={tabDialogOpen} onOpenChange={(open) => !isSavingTab && setTabDialogOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tabBeingRenamed ? 'Rename tab' : 'Add tab'}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={tabNameInput}
            onChange={(e) => setTabNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void submitTabDialog()
              }
            }}
            placeholder='Tab name'
            disabled={isSavingTab}
          />
          <DialogFooter>
            <Button variant='outline' onClick={() => setTabDialogOpen(false)} disabled={isSavingTab}>
              Cancel
            </Button>
            <Button onClick={submitTabDialog} isLoading={isSavingTab} disabled={!tabNameInput.trim()}>
              {tabBeingRenamed ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pasteConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !isPasting) setPasteConfirmOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Paste {clipboardTabCount} tab{clipboardTabCount === 1 ? '' : 's'} into this order?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The copied tabs are added alongside the {tabs.length} tab
              {tabs.length === 1 ? '' : 's'} already here — nothing is replaced. The copied checklist
              stays available for other orders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPasting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isPasting}
              onClick={(e) => {
                e.preventDefault()
                void pasteAllTabs()
              }}
            >
              {isPasting ? <LoaderCircle className='w-4 h-4 animate-spin' /> : 'Paste'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={tabPendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeletingTab) setTabPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{tabPendingDelete?.tab_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This checklist tab and its {tabPendingDelete?.checklist.length ?? 0} item
              {(tabPendingDelete?.checklist.length ?? 0) === 1 ? '' : 's'} will be removed. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingTab}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeletingTab}
              className='bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20'
              onClick={(e) => {
                e.preventDefault()
                void confirmDeleteTab()
              }}
            >
              {isDeletingTab ? <LoaderCircle className='w-4 h-4 animate-spin' /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ChecklistTabs
